import { z } from 'zod';
import {
  dateRangeFields,
  isOrderedRange,
  ORDERED_RANGE_ISSUE,
  isoDateTimeSchema,
  longTextSchema,
  nonNegativeMoneySchema,
  paginationSchema,
  positiveMoneySchema,
  shortTextSchema,
  sortOrderSchema,
  uuidSchema,
} from '../primitives.js';
import {
  PAYMENT_METHODS,
  PAYMENT_STATUSES,
  SALE_SOURCES,
  SALE_SOURCE_SYSTEMS,
} from '../enums.js';

export const createSaleSchema = z.object({
  amount: positiveMoneySchema,
  description: longTextSchema.optional(),
  customerId: uuidSchema.nullable().optional(),
  branchId: uuidSchema.nullable().optional(),
  /** Configured sales channel. Falls back to the `source` enum when absent. */
  channelId: uuidSchema.nullable().optional(),
  /** Optional catalogue item — one per sale. */
  productId: uuidSchema.nullable().optional(),
  /** The staff member credited with the sale, not whoever typed it in. */
  staffId: uuidSchema.nullable().optional(),
  paymentMethod: z.enum(PAYMENT_METHODS),
  paymentStatus: z.enum(PAYMENT_STATUSES).default('paid'),
  /** Amount already received. Defaults to the full amount when status is `paid`. */
  amountPaid: nonNegativeMoneySchema.optional(),
  source: z.enum(SALE_SOURCES).default('manual'),
  /**
   * Cost of goods for this sale, when known. Optional by design: gross profit
   * is reported only over the sales that actually carry cost data, so a
   * business that never enters costs still gets correct revenue reporting.
   */
  costAmount: nonNegativeMoneySchema.nullable().optional(),
  occurredAt: isoDateTimeSchema.optional(),
  /**
   * Caller-supplied unique key for non-manual sources. Lets a WhatsApp bot,
   * POS terminal or API client retry safely without double-recording revenue.
   */
  externalRef: z.string().trim().max(128).nullable().optional(),
  /**
   * Provenance, for a sale that originated in another system.
   *
   * Distinct from `source`, which is the channel. Together with
   * `sourceRecordId` these form the idempotency key an external export is
   * re-run against: `sales_source_record_key` on
   * (business_id, source_system, source_record_id).
   *
   * Not reachable from the public create endpoint — the importer sets them —
   * so a client cannot claim a sale came from a system it did not.
   */
  sourceSystem: z.enum(SALE_SOURCE_SYSTEMS).optional(),
  sourceRecordId: z.string().trim().max(256).nullable().optional(),
  importBatchId: uuidSchema.nullable().optional(),
  notes: longTextSchema.optional(),
});
export type CreateSaleInput = z.infer<typeof createSaleSchema>;

export const updateSaleSchema = createSaleSchema
  .omit({ source: true, externalRef: true, amountPaid: true })
  .partial()
  .extend({
    /**
     * Why an imported sale is being corrected. Required in a pharmacy, where
     * correcting imported history is the owner's alone and is kept with the
     * before and after; ignored elsewhere.
     */
    correctionReason: shortTextSchema.optional(),
  });
export type UpdateSaleInput = z.infer<typeof updateSaleSchema>;

/** Recording money received against an existing sale. */
export const recordPaymentSchema = z.object({
  amount: positiveMoneySchema,
  method: z.enum(PAYMENT_METHODS),
  paidAt: isoDateTimeSchema.optional(),
  reference: z.string().trim().max(128).optional(),
  note: longTextSchema.optional(),
  /**
   * In a pharmacy, a card, transfer or terminal payment counts only when the
   * person recording it has seen the slip or the bank alert, and says so here.
   * A transfer also needs its `reference`. Ignored everywhere else.
   */
  confirmed: z.boolean().optional(),
});
export type RecordPaymentInput = z.infer<typeof recordPaymentSchema>;

export const voidSaleSchema = z.object({
  reason: shortTextSchema,
});
export type VoidSaleInput = z.infer<typeof voidSaleSchema>;

export const saleFiltersSchema = paginationSchema
  .merge(dateRangeFields)
  .extend({
    search: z.string().trim().max(120).optional(),
    customerId: uuidSchema.optional(),
    branchId: uuidSchema.optional(),
    channelId: uuidSchema.optional(),
    productId: uuidSchema.optional(),
    staffId: uuidSchema.optional(),
    source: z.enum(SALE_SOURCES).optional(),
    paymentMethod: z.enum(PAYMENT_METHODS).optional(),
    paymentStatus: z.enum(PAYMENT_STATUSES).optional(),
    /** Only sales with money still outstanding. */
    outstandingOnly: z.coerce.boolean().optional(),
    sortBy: z.enum(['occurredAt', 'amount', 'createdAt']).default('occurredAt'),
    sortOrder: sortOrderSchema,
  })
  // An inverted range used to return an empty page and no complaint, which
  // reads exactly like "you have no sales".
  .refine((v: { from?: string | undefined; to?: string | undefined }) => isOrderedRange(v), { message: ORDERED_RANGE_ISSUE.message, path: ['from'] });
export type SaleFilters = z.infer<typeof saleFiltersSchema>;

export const salePaymentSchema = z.object({
  id: uuidSchema,
  saleId: uuidSchema,
  amount: z.string(),
  method: z.enum(PAYMENT_METHODS),
  reference: z.string().nullable(),
  note: z.string().nullable(),
  paidAt: z.string(),
  recordedByUserId: uuidSchema.nullable(),
  createdAt: z.string(),
});
export type SalePayment = z.infer<typeof salePaymentSchema>;

export const saleSchema = z.object({
  id: uuidSchema,
  businessId: uuidSchema,
  reference: z.string(),
  amount: z.string(),
  currencyCode: z.string(),
  description: z.string().nullable(),
  notes: z.string().nullable(),
  customerId: uuidSchema.nullable(),
  customerName: z.string().nullable(),
  branchId: uuidSchema.nullable(),
  branchName: z.string().nullable(),
  channelId: uuidSchema.nullable(),
  channelName: z.string().nullable(),
  productId: uuidSchema.nullable(),
  productName: z.string().nullable(),
  staffId: uuidSchema.nullable(),
  staffName: z.string().nullable(),
  paymentMethod: z.enum(PAYMENT_METHODS),
  paymentStatus: z.enum(PAYMENT_STATUSES),
  source: z.enum(SALE_SOURCES),
  externalRef: z.string().nullable(),
  costAmount: z.string().nullable(),
  /** Sum of `sale_payments`. Derived, never stored. */
  amountPaid: z.string(),
  /** `amount - amountPaid`, floored at zero. */
  amountOutstanding: z.string(),
  occurredAt: z.string(),
  recordedByUserId: uuidSchema.nullable(),
  recordedByName: z.string().nullable(),
  voidedAt: z.string().nullable(),
  voidReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Sale = z.infer<typeof saleSchema>;
